import { GridColDef } from '@mui/x-data-grid';
import React from 'react';

import DataGrid from '#app/Component/DataGrid';
import Guest from '#app/Component/Guest';
import IconAction from '#app/Component/IconAction';
import { getRoutePathname } from '#app/Handler/RouteHandler';
import { transformObjectToDataGridRowsArray } from '#app/Util/DataGridDataTransformer';
import { momentDateFormatter } from '#app/Util/MomentDateFormatter';
import useOrderClientList from '#app/View/Sale/OrderClient/SaleOrderClientList/hooks/useOrderClientList';
import { Channel, ChannelTranslations } from '#app/typings/channel';
import { OrderClientList, OrderClientState, OrderClientStateTranslation } from '#app/typings/orderClient';

type Props = Omit<ReturnType<typeof useOrderClientList>, 'setFilters' | 'filters'>;

const List: React.FC<Props> = ({
  data: orderClients,
  totalItems,
  loading,
  pagination,
  sorting,
  setPagination,
  setSorting,
}) => {
  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: 'Référence',
      minWidth: 160,
      sortable: false,
    },
    {
      field: 'clientEmail',
      headerName: 'Email',
      valueGetter: (_, row: OrderClientList) => row.client?.email,
      flex: 1,
      sortable: false,
    },
    {
      field: 'clientName',
      headerName: 'Client',
      valueGetter: (_, row: OrderClientList) => row.client?.name,
      flex: 1,
      sortable: false,
    },
    {
      field: 'numberSylius',
      headerName: 'Num sylius',
      valueGetter: (value) => value ?? '-',
      sortable: false,
    },
    {
      field: 'channel',
      headerName: 'Canal',
      valueGetter: (value: Channel) => ChannelTranslations[value],
      sortable: false,
    },
    {
      field: 'carrier',
      headerName: 'Transporteur',
      valueGetter: (_, row: OrderClientList) => row.orderClientCarrier?.label ?? '-',
      sortable: false,
      minWidth: 120,
    },
    {
      field: 'orderClientState',
      headerName: 'Etat',
      valueGetter: (value: OrderClientState) => OrderClientStateTranslation[value],
    },
    {
      field: 'createdAt',
      headerName: 'Date de création',
      valueGetter: (value: string) => momentDateFormatter.dateTimeToFormatFr(value),
      minWidth: 130,
    },
    {
      field: 'confirmedAt',
      headerName: 'Date de commande',
      valueGetter: (value: string) => (value ? momentDateFormatter.dateTimeToFormatFr(value) : undefined),
      minWidth: 130,
    },
    {
      field: 'isGuest',
      headerName: 'Guest',
      renderCell: ({ value }) => <Guest isGuest={value} />,
      sortable: false,
    },
    {
      field: 'action',
      headerName: 'Action',
      type: 'actions',
      getActions: ({ row }) => [
        <IconAction title="Visualiser" icon="view" to={getRoutePathname('sale_order_client', { uuid: row.uuid })} />,
      ],
    },
  ];

  return (
    <DataGrid
      rows={transformObjectToDataGridRowsArray<OrderClientList>(orderClients)}
      columns={columns}
      loading={loading}
      rowCount={totalItems}
      setSorting={setSorting}
      setPagination={setPagination}
      pagination={pagination}
      sorting={sorting}
    />
  );
};

export default List;
